/**
 * WalletConnect Component
 * 
 * Provides wallet connection UI with staking status badge.
 * Part of the Mango Group ecosystem.
 */

import { memo, useCallback, useMemo } from "react";
import { useAppKit } from "@reown/appkit/react";
import { useAccount, useBalance, useChainId, useDisconnect } from "wagmi";
import { Wallet, ChevronDown, Coins, Shield, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { useStaking, shortenAddress, formatTokenAmount } from "@/hooks/useWeb3";
import { getChainInfo, MANGO_CONTRACTS, type SupportedChainId } from "@/lib/web3";
import { cn } from "@/lib/utils";

interface WalletConnectProps {
  /** Show full button or compact icon */
  variant?: "full" | "compact" | "icon";
  /** Additional class names */
  className?: string;
  /** Show staking tier badge */
  showTierBadge?: boolean;
}

/**
 * Staking tier type
 */
type StakingTier = "none" | "bronze" | "silver" | "gold" | "platinum" | "diamond";

/**
 * Tier badge colors
 */
const TIER_COLORS: Record<StakingTier, string> = {
  none: "bg-gray-100 text-gray-600",
  bronze: "bg-amber-100 text-amber-700",
  silver: "bg-slate-200 text-slate-700",
  gold: "bg-yellow-100 text-yellow-700",
  platinum: "bg-purple-100 text-purple-700",
  diamond: "bg-cyan-100 text-cyan-700",
};

/**
 * Tier icons
 */
const TIER_ICONS: Record<StakingTier, string | null> = {
  none: null,
  bronze: "🥉",
  silver: "🥈",
  gold: "🥇",
  platinum: "💎",
  diamond: "👑",
};

/**
 * WalletConnect button component
 * Handles wallet connection and displays staking status
 */
export const WalletConnect = memo(function WalletConnect({
  variant = "full",
  className,
  showTierBadge = true,
}: WalletConnectProps) {
  const { open } = useAppKit();
  const { address, isConnected } = useAccount();
  const chainId = useChainId();
  const { disconnect } = useDisconnect();
  const { tier: rawTier, totalStaked, feeDiscount } = useStaking();
  
  // Type-safe tier
  const tier = (rawTier || "none") as StakingTier;

  // Get $MANGO token address for current chain
  const mangoTokenAddress = useMemo(() => {
    const contracts = MANGO_CONTRACTS[chainId as SupportedChainId];
    return contracts?.token as `0x${string}` | undefined;
  }, [chainId]);

  // Get native balance first (ETH/MATIC)
  const { data: nativeBalance } = useBalance({
    address,
  });

  // TODO: For ERC-20 token balance, use readContract with ERC-20 ABI
  // wagmi v3 useBalance only supports native tokens
  // In production, use: useReadContract({ abi: erc20Abi, functionName: 'balanceOf', args: [address] })
  const mangoBalance = useMemo(() => {
    // Placeholder - will be replaced with actual ERC-20 balance read
    return null;
  }, []);

  const chainInfo = useMemo(() => getChainInfo(chainId), [chainId]);

  const handleDisconnect = useCallback(() => {
    disconnect();
  }, [disconnect]);

  // Icon-only variant
  if (variant === "icon") {
    return (
        <Button
            variant="ghost"
            size="icon"
            onClick={() => open()}
            className={className}
            aria-label={isConnected ? "Wallet connected" : "Connect wallet"}
        >
            <Wallet className={cn("h-5 w-5", isConnected && "text-green-500")} />
        </Button>
    );
  }

  // Not connected state
  if (!isConnected) {
    return (
        <Button
            onClick={() => open()}
            variant="outline"
            className={cn("gap-2", className)}
        >
            <Wallet className="h-4 w-4" />
            {variant === "full" && "Connect Wallet"}
        </Button>
    );
  }

  // Connected state with dropdown
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className={cn("gap-2", className)}>
          {/* Tier badge */}
          {showTierBadge && tier !== "none" && (
            <span className="text-sm">{TIER_ICONS[tier]}</span>
          )}
          
          {/* Address */}
          <span className="font-mono text-sm">
            {address ? shortenAddress(address) : "Connected"}
          </span>
          
          {/* Chain indicator */}
          {chainInfo && (
            <Badge variant="secondary" className="text-xs px-1.5 py-0">
              {chainInfo.name}
            </Badge>
          )}
          
          <ChevronDown className="h-4 w-4 opacity-50" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium">Connected Wallet</p>
            <p className="text-xs text-muted-foreground font-mono">
              {address ? shortenAddress(address, 8) : ""}
            </p>
          </div>
        </DropdownMenuLabel>

        <DropdownMenuSeparator />

        {/* $MANGO Balance */}
        <DropdownMenuItem className="flex items-center justify-between cursor-default">
          <div className="flex items-center gap-2">
            <Coins className="h-4 w-4 text-orange-500" />
            <span>$MANGO Balance</span>
          </div>
          <span className="font-mono text-sm">
            {totalStaked !== "0" ? formatTokenAmount(totalStaked) : "0"}
          </span>
        </DropdownMenuItem>

        {/* Staking Tier */}
        <DropdownMenuItem className="flex items-center justify-between cursor-default">
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4 text-purple-500" />
            <span>Staking Tier</span>
          </div>
          <Badge className={cn("text-xs", TIER_COLORS[tier])}>
            {TIER_ICONS[tier]} {tier.charAt(0).toUpperCase() + tier.slice(1)}
          </Badge>
        </DropdownMenuItem>

        {/* Fee Discount */}
        {feeDiscount.discount > 0 && (
          <DropdownMenuItem className="flex items-center justify-between cursor-default">
            <span className="text-sm text-muted-foreground">Fee Discount</span>
            <Badge variant="outline" className="text-xs text-green-600">
              {feeDiscount.discount}% off
            </Badge>
          </DropdownMenuItem>
        )}

        {/* Staked Amount */}
        {BigInt(totalStaked) > BigInt(0) && (
          <DropdownMenuItem className="flex items-center justify-between cursor-default">
            <span className="text-sm text-muted-foreground">Staked</span>
            <span className="font-mono text-xs">
              {formatTokenAmount(totalStaked)} $MANGO
            </span>
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />

        {/* Actions */}
        <DropdownMenuItem asChild>
          <a href="/dashboard/wallet" className="cursor-pointer">
            <Wallet className="h-4 w-4 mr-2" />
            Manage Wallet
          </a>
        </DropdownMenuItem>

        <DropdownMenuItem asChild>
          <a href="/dashboard/staking" className="cursor-pointer">
            <Coins className="h-4 w-4 mr-2" />
            Staking Dashboard
          </a>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem
          onClick={handleDisconnect}
          className="text-red-600 focus:text-red-600 cursor-pointer"
        >
          <LogOut className="h-4 w-4 mr-2" />
          Disconnect
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
});

WalletConnect.displayName = "WalletConnect";

/**
 * Simple connect button without dropdown
 */
export const ConnectButton = memo(function ConnectButton({
  className,
  children,
}: {
  className?: string;
  children?: React.ReactNode;
}) {
  const { open } = useAppKit();
  const { isConnected, address } = useAccount();

  return (
    <Button
        onClick={() => open()}
        variant={isConnected ? "outline" : "default"}
        className={cn("gap-2", className)}
    >
        <Wallet className="h-4 w-4" />
        {children ?? (isConnected ? shortenAddress(address ?? "") : "Connect Wallet")}
    </Button>
  );
});

ConnectButton.displayName = "ConnectButton";

export default WalletConnect;
